import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function About() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("terms");

  const stats = t("aboutPage.stats", { returnObjects: true });
  const termsItems = t("aboutPage.legal.terms.items", { returnObjects: true });

  // Tab Styling Helper
  const getTabStyle = (tabId) => ({
    padding: "12px 24px",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    background: activeTab === tabId ? "#ffffff" : "transparent",
    color: activeTab === tabId ? "#0d7a5f" : "#64748b",
    border: "none",
    borderRadius: "8px",
    boxShadow: activeTab === tabId ? "0 4px 12px rgba(13,122,95,0.08)" : "none",
    transition: "all 0.2s ease"
  });

  return (
    <div className="page about-page" style={{ backgroundColor: "#f4f9f6", minHeight: "100vh" }}>
      
      <style>{`
        .about-page, 
        .about-page .page-hero, 
        .about-page .section-block, 
        .about-page .stats-section {
          background-color: #f4f9f6 !important;
        }
      `}</style>

      {/* ===== PAGE HERO ===== */}
      <section className="page-hero">
        <div className="section-label">{t("aboutPage.heroLabel")}</div>
        <h1>{t("aboutPage.heroTitle")}</h1>
        <p>{t("aboutPage.heroSubtitle")}</p>
      </section>

      {/* ===== BLOCK 1: OUR IDENTITY ===== */}
      <section className="section-block">
        <div className="container">
          <div className="about-grid">
            <div className="about-img-box">
              <span className="about-emoji">💊</span>
              <div className="about-img-glow"></div>
            </div>
            <div className="about-text">
              <div className="section-label">{t("aboutPage.identityLabel")}</div>
              <h2>{t("aboutPage.identityTitle")}</h2>
              <p>{t("aboutPage.identityPara1")}</p>
              <p>{t("aboutPage.identityPara2")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BLOCK 2: STATS ===== */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {Array.isArray(stats) && stats.map((stat, i) => (
              <div key={i} className="stat-card">
                <h3>{stat.value}</h3>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BLOCK 3: CORE VALUES ===== */}
      <section className="section-block">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{t("aboutPage.coreValuesLabel")}</div>
            <h2>{t("aboutPage.coreValuesTitle")}</h2>
          </div>
          <div className="cards-row">
            <div className="card">
              <div className="card-icon">🏗️</div>
              <h3>{t("aboutPage.card1Title")}</h3>
              <p>{t("aboutPage.card1Desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">⚡</div>
              <h3>{t("aboutPage.card2Title")}</h3>
              <p>{t("aboutPage.card2Desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">🛡️</div>
              <h3>{t("aboutPage.card3Title")}</h3>
              <p>{t("aboutPage.card3Desc")}</p>
            </div>
          </div>
          <div className="center-btn" style={{ marginBottom: "50px" }}>
            <Link to="/contact" className="btn-primary">{t("aboutPage.partnerBtn")}</Link>
          </div>
        </div>
      </section>

      {/* ===== BLOCK 4: LEGAL COMPLIANCE & POLICIES ===== */}
      <section style={{ padding: "60px 20px", background: "#f4f9f6", borderTop: "1px solid #e2eaf0" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          
          {/* Section Headers */}
          <div style={{ textAlign: "center", marginBottom: "35px" }}>
            <span style={{ fontSize: "0.85rem", background: "#d1e7dd", color: "#0d7a5f", padding: "6px 16px", borderRadius: "20px", fontWeight: "700", letterSpacing: "1px", textTransform: "uppercase" }}>
              {t("aboutPage.legal.badge")}
            </span>
            <h2 style={{ color: "#0f172a", marginTop: "15px", fontSize: "2rem" }}>{t("aboutPage.legal.title")}</h2>
            <p style={{ color: "#64748b", fontSize: "0.95rem" }}>{t("aboutPage.legal.subtitle")}</p>
          </div>

          {/* Navigation Tabs Header */}
          <div style={{ display: "flex", justifySelf: "center", background: "#e2eaf0", padding: "6px", borderRadius: "10px", marginBottom: "25px", gap: "5px" }}>
            <button onClick={() => setActiveTab("terms")} style={getTabStyle("terms")}>{t("aboutPage.legal.tabs.terms")}</button>
            <button onClick={() => setActiveTab("privacy")} style={getTabStyle("privacy")}>{t("aboutPage.legal.tabs.privacy")}</button>
            <button onClick={() => setActiveTab("refund")} style={getTabStyle("refund")}>{t("aboutPage.legal.tabs.refund")}</button>
          </div>

          {/* Tab Display Panel Window */}
          <div style={{ background: "#ffffff", padding: "40px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(13,122,95,0.03)", border: "1px solid #e2eaf0", minHeight: "400px" }}>
            
            {/* TERMS AND CONDITIONS TAB CONTENT */}
            {activeTab === "terms" && (
              <div>
                <div style={{ borderBottom: "2px solid #f4f9f6", paddingBottom: "15px", marginBottom: "25px" }}>
                  <h3 style={{ margin: 0, color: "#0f172a", fontSize: "1.4rem" }}>{t("aboutPage.legal.terms.title")}</h3>
                </div>
                <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6", marginBottom: "20px" }}>
                  {t("aboutPage.legal.terms.intro1")} <strong>{t("logo.main")} {t("logo.sub")}</strong>{t("aboutPage.legal.terms.intro2")}
                </p>
                
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {Array.isArray(termsItems) && termsItems.map((item) => (
                    <div key={item.num} style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "24px", height: "24px", borderRadius: "50%", background: "#e8f5e9", color: "#0d7a5f", fontWeight: "700", fontSize: "0.85rem", marginTop: "2px" }}>
                        {item.num}
                      </span>
                      <div>
                        <h4 style={{ margin: "0 0 4px 0", color: "#1e293b", fontSize: "1rem", fontWeight: "600" }}>{item.title}</h4>
                        <p style={{ margin: 0, color: "#4a5568", fontSize: "0.9rem", lineHeight: "1.5" }}>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "30px", borderTop: "2px solid #f4f9f6", paddingTop: "20px" }}>
                  <span style={{ fontSize: "0.85rem", color: "#0d7a5f", background: "#e8f5e9", padding: "6px 14px", borderRadius: "6px", fontWeight: "500" }}>
                    {t("aboutPage.legal.terms.lastUpdated")}
                  </span>
                </div>
              </div>
            )}

            {/* PRIVACY POLICY TAB CONTENT */}
            {activeTab === "privacy" && (
              <div>
                <h3 style={{ borderBottom: "2px solid #f4f9f6", paddingBottom: "15px", marginBottom: "20px", color: "#0f172a", fontSize: "1.4rem" }}>{t("aboutPage.legal.privacy.title")}</h3>
                <div style={{ display: "flex", gap: "15px", background: "#f4f9f6", padding: "20px", borderRadius: "8px", border: "1px solid #d1e7dd", marginBottom: "20px" }}>
                  <span style={{ fontSize: "1.5rem" }}>🔒</span>
                  <p style={{ margin: 0, color: "#334155", fontSize: "0.95rem", lineHeight: "1.6" }}>
                    {t("aboutPage.legal.privacy.alertText")}
                  </p>
                </div>
                <h4 style={{ color: "#1e293b", fontSize: "1.1rem", marginBottom: "10px" }}>{t("aboutPage.legal.privacy.subheading")}</h4>
                <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                  {t("aboutPage.legal.privacy.text")}
                </p>
              </div>
            )}

            {/* REFUND POLICY TAB CONTENT */}
            {activeTab === "refund" && (
              <div>
                <h3 style={{ borderBottom: "2px solid #f4f9f6", paddingBottom: "15px", marginBottom: "20px", color: "#0f172a", fontSize: "1.4rem" }}>{t("aboutPage.legal.refund.title")}</h3>
                
                <div style={{ background: "#fff1f2", border: "1px solid #ffe4e6", padding: "20px", borderRadius: "8px", marginBottom: "25px", display: "flex", gap: "15px" }}>
                  <span style={{ fontSize: "1.5rem" }}>⚠️</span>
                  <div>
                    <h4 style={{ margin: "0 0 5px 0", color: "#9f1239", fontSize: "1.05rem" }}>{t("aboutPage.legal.refund.alertTitle")}</h4>
                    <p style={{ margin: 0, color: "#9f1239", fontSize: "0.95rem", lineHeight: "1.5" }}>
                      {t("aboutPage.legal.refund.alertText")}
                    </p>
                  </div>
                </div>

                <h4 style={{ color: "#1e293b", fontSize: "1.1rem", marginBottom: "10px" }}>{t("aboutPage.legal.refund.subheading")}</h4>
                <p style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.6" }}>
                  {t("aboutPage.legal.refund.text")}
                </p>
                <ul style={{ paddingLeft: "20px", color: "#4a5568", lineHeight: "1.8", fontSize: "0.95rem" }}>
                  <li>{t("aboutPage.legal.refund.bullet1")}</li>
                </ul>
              </div>
            )}

          </div>

        </div>
      </section>

    </div>
  );
}

export default About;